import { useCallback, useEffect, useState } from 'react';
import api from '../lib/api';
import { log } from '../lib/logger';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, ClipboardList, TrendingUp, Info,
  ArrowRight, Loader2, RefreshCcw, Clock, Calendar, AlertCircle, Database, Download, Trash2, ShieldAlert, Cloud, CloudOff, ExternalLink, Zap, Archive,
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { fmtDateTime } from '../lib/date';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import ConfirmDestructive from '../components/ConfirmDestructive';

// ────────────────────────────────────────────────────────────────────────────
// The Data Import Center is the single place for every recurring upload. Each
// SECTION groups related reports, each REPORT is a single file with a strict
// endpoint and — critically — a documented time-window ("upload the whole
// year every time" vs "last 90 days"), because payment data mutates for months
// after a visit while attendance & note data don't.
// ────────────────────────────────────────────────────────────────────────────
const WINDOWS = {
  full_year:  { label: 'FULL YEAR (REPLACE-IN-RANGE)', hint: 'Upload the file — the system deletes existing rows in the file\'s date range and inserts fresh ones (data outside the range is preserved). You can safely re-upload the same range, or upload in 3-month chunks for large practices to keep each upload fast.', tone: '#8A3A3A', icon: Calendar },
  ninety_day: { label: 'ROLLING (UPSERT)',   hint: 'Recent visits only. Existing rows in the same date range get their fields refreshed; older data is preserved untouched.', tone: '#685B72', icon: Clock },
  monthly:    { label: 'MONTHLY SNAPSHOT',   hint: 'Replaces the covered month. Upload the newest month; other months untouched.', tone: '#839E97', icon: Clock },
  weekly:     { label: 'WEEKLY',             hint: 'Upload weekly; system upserts by natural key so re-uploading the same range is safe.', tone: '#839E97', icon: Clock },
};

const SECTIONS = [
  {
    id: 'unified',
    title: 'Data Import',
    subtitle: 'One file per report — the same upload feeds every downstream module (BI, Performance, Pending Notes, Compliance).',
    icon: ClipboardList,
    tone: '#685B72',
    autoRefresh: '/bi/refresh',
    reports: [
      {
        id: 'client-attendance',
        name: 'Client Attendance Report',
        window: 'full_year',
        endpoints: [
          { url: '/bi/upload', params: { report_type: 'attendance' }, label: 'Executive BI' },
          { url: '/performance/upload/attendance',                     label: 'Performance' },
        ],
        accept: '.xlsx,.csv',
        exportHint: 'Reports → Client Attendance Report → Jan 1 → today (or 3-month chunks)',
        expectedType: 'Show/No-show data',
        expectedCols: ['client_name', 'clinician_name', 'date_of_service', 'office_name', 'status'],
      },
      {
        id: 'appointment-status',
        name: 'Appointment Status Report',
        window: 'full_year',
        // Column J contains progress-note status → same file also feeds
        // Ops/Pending Notes + Progress Notes compliance in one drop.
        endpoints: [
          { url: '/bi/upload', params: { report_type: 'appt_status' }, label: 'Executive BI' },
          { url: '/performance/upload/appointments',                    label: 'Performance' },
          { url: '/import/simplepractice',                              label: 'Pending Notes + Compliance' },
        ],
        accept: '.xlsx,.csv',
        exportHint: 'Reports → Appointment Status Report → Jan 1 → today (or 3-month chunks)',
        expectedType: 'CPT + payments + progress-note status',
        expectedCols: ['Client', 'Clinician', 'Date of Service', 'Billing Code', 'Progress Note', 'Client Paid'],
      },
      {
        id: 'claims',
        name: 'Claims Report',
        window: 'full_year',
        endpoints: [{ url: '/bi/upload', params: { report_type: 'claims' }, label: 'Executive BI' }],
        accept: '.xlsx,.csv',
        exportHint: 'Reports → Claims → Jan 1 → today (or 3-month chunks)',
        expectedType: 'Claim status & payer',
        expectedCols: ['Client', 'Payer', 'Date of Service', 'Status', 'Billed', 'Paid'],
      },
      {
        id: 'income',
        name: 'Income & Fees',
        window: 'full_year',
        endpoints: [{ url: '/bi/upload', params: { report_type: 'income' }, label: 'Executive BI' }],
        accept: '.xlsx,.csv',
        exportHint: 'Reports → Monthly Income Summary → Jan 1 → today',
        expectedType: 'Monthly totals',
        expectedCols: ['Month', 'Insurance Income', 'Client Income', 'Total Income'],
      },
      {
        id: 'payer-alloc',
        name: 'Payer Allocation',
        window: 'monthly',
        endpoints: [{ url: '/bi/upload', params: { report_type: 'payer_alloc' }, label: 'Executive BI' }],
        accept: '.xlsx,.csv',
        exportHint: 'Reports → Payer Allocation → current snapshot',
        expectedType: 'Per-clinician allocation',
        expectedCols: ['clinician_name', 'payer_group', 'provider', 'total_paid'],
      },
    ],
    audience: 'super',
  },
];

function TimeWindowBadge({ window }) {
  const w = WINDOWS[window]; if (!w) return null;
  const Icon = w.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest"
      style={{ background: `${w.tone}18`, color: w.tone }}
      title={w.hint}
    >
      <Icon size={10} strokeWidth={2.4} />
      {w.label}
    </span>
  );
}


function ReportDropCard({ report, tone, autoRefresh, disabled, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const w = WINDOWS[report.window];

  const submit = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true); setResult(null); setProgress(null);

    const endpoints = report.endpoints ||
      [{ url: report.endpoint, params: report.params, label: report.name }];

    const breakdown = [];
    let success = true, firstError = null;

    // Helper: POST a single endpoint with optional shrink-guardrail confirm loop.
    // If the server returns 409 with code=shrink_warning, prompt the user with
    // the numbers and, on OK, retry the same request with confirm=true.
    const postWithGuardrail = async (baseUrl, params, fileBlob, epLabel) => {
      const buildUrl = (extraConfirm) => {
        const p = new URLSearchParams(params || {});
        if (extraConfirm) p.set('confirm', 'true');
        const qs = p.toString();
        return qs ? `${baseUrl}?${qs}` : baseUrl;
      };
      // Poll ingest_jobs.status until 'complete' or 'failed'. Returns the
      // last recorded job doc so the caller can extract rows_done / result.
      const pollBatch = async (batchId, epLabel) => {
        // ~5 min hard cap = 150 polls × 2s each
        for (let i = 0; i < 150; i++) {
          await new Promise(res => setTimeout(res, 2000));
          try {
            const s = await api.get(`/imports/status/${batchId}`);
            const doc = s.data;
            if (doc?.rows_total != null && doc?.rows_done != null) {
              setProgress(`${epLabel} — ${doc.rows_done.toLocaleString()} / ${doc.rows_total.toLocaleString()} rows`);
            }
            if (doc?.status === 'complete') return doc;
            if (doc?.status === 'shrink_warning') {
              // Background runner detected a shrink >50% and paused. Reshape
              // the error to match the sync HTTP-409 contract so the outer
              // try/catch can prompt the user for confirmation + resubmit.
              const err = new Error('shrink_warning');
              err.response = { status: 409, data: { detail: doc.shrink_detail } };
              throw err;
            }
            if (doc?.status === 'failed') {
              const err = new Error(doc.error || 'Ingest failed');
              err.response = { data: { detail: doc.error || 'Ingest failed' } };
              throw err;
            }
          } catch (e) {
            if (e?.response?.status === 404) continue;   // job doc not yet visible
            throw e;
          }
        }
        throw new Error('Ingest timed out after 5 minutes — check /api/imports/recent');
      };

      const send = async (confirmed) => {
        const fd = new FormData();
        fd.append('file', fileBlob);
        const initial = await api.post(buildUrl(confirmed), fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000,
        });
        // If the endpoint returns a batch_id (background job), poll status.
        // Legacy endpoints returning inline results (bi/upload, income) fall
        // through unchanged.
        if (initial.data?.batch_id && initial.data?.status === 'processing') {
          const doneDoc = await pollBatch(initial.data.batch_id, epLabel);
          return { data: doneDoc.result || { ok: true, rows_parsed: doneDoc.rows_done } };
        }
        return initial;
      };
      try {
        return await send(false);
      } catch (err) {
        const d = err?.response?.data?.detail;
        if (err?.response?.status === 409 && d?.code === 'shrink_warning') {
          const proceed = window.confirm(
            `${d.message}\n\nClick OK to REPLACE ${d.existing_rows.toLocaleString()} rows ` +
            `with ${d.incoming_rows.toLocaleString()} from this file.\nClick Cancel to abort.`
          );
          if (!proceed) {
            const abortErr = new Error('Upload aborted — kept existing rows.');
            abortErr.aborted = true;
            throw abortErr;
          }
          toast.info(`Proceeding with ${epLabel} despite shrink warning…`);
          return await send(true);
        }
        throw err;
      }
    };

    for (const ep of endpoints) {
      setProgress(ep.label);
      try {
        const r = await postWithGuardrail(ep.url, ep.params, f, ep.label);
        const rows = r.data?.rows_parsed ?? r.data?.rows ?? r.data?.upserted ?? 0;
        breakdown.push({ label: ep.label, rows, ok: true });
      } catch (err) {
        success = false;
        const detail = err?.aborted
          ? 'Upload cancelled by user (existing data kept).'
          : (err?.response?.data?.detail?.message || err?.response?.data?.detail || err?.message || 'Upload failed');
        breakdown.push({ label: ep.label, ok: false, msg: detail });
        if (!firstError) firstError = detail;
      }
    }
    setProgress(null);
    setResult({ ok: success, breakdown, msg: firstError });
    if (success) {
      toast.success(`${report.name} uploaded successfully.`);
      if (autoRefresh) {
        // Cube refresh runs in the background — kick it, then poll status.
        // The compute itself can take 30-90s on a large practice; polling
        // means Cloudflare's 100s edge timeout never fires and the user
        // gets a clean success instead of the "refresh failed" toast.
        setProgress('Refreshing BI cube…');
        try {
          const kick = await api.post(autoRefresh, {}, { timeout: 15000 });
          const bid = kick.data?.batch_id;
          if (bid && kick.data?.status === 'processing') {
            // Poll up to 5 min. Same cadence as the upload poller.
            for (let i = 0; i < 150; i++) {
              await new Promise(res => setTimeout(res, 2000));
              const s = await api.get(`/imports/status/${bid}`);
              const doc = s.data;
              if (doc?.rows_total && doc?.rows_done != null) {
                setProgress(`Refreshing BI cube — step ${doc.rows_done} of ${doc.rows_total}`);
              }
              if (doc?.status === 'complete') { setProgress(null); break; }
              if (doc?.status === 'failed') throw new Error(doc.error || 'Cube refresh failed');
            }
          }
          // Legacy sync response — nothing else to do.
        } catch (err) {
          const msg = err?.response?.data?.detail || err?.message || 'Cube refresh failed';
          log.warn(`Auto-refresh (${autoRefresh}) failed after upload:`, err);
          toast.warning(`Uploaded, but the cube refresh failed: ${msg}. Hit "Refresh Cube" manually.`);
        } finally {
          setProgress(null);
        }
      }
      onDone?.();
    } else {
      toast.error(`${report.name}: ${firstError}`);
    }
    setBusy(false);
    e.target.value = '';
  };

  const dz = disabled ? 'opacity-50 pointer-events-none' : '';
  return (
    <label
      data-testid={`import-drop-${report.id}`}
      className={`block relative rounded-2xl border-2 border-dashed cursor-pointer transition-all p-4 ${busy ? 'opacity-90 pointer-events-none' : 'hover:bg-white/70'} ${dz}`}
      style={{ borderColor: `${tone}55`, background: `${tone}0A` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm" style={{ color: '#2D2833' }}>{report.name}</span>
            <TimeWindowBadge window={report.window} />
          </div>
          {report.exportHint && (
            <div className="text-[11px] mt-1.5 flex items-center gap-1" style={{ color: '#685B72' }}>
              <FileSpreadsheet size={11} /> Export from SimplePractice: <span className="italic">{report.exportHint}</span>
            </div>
          )}
          {report.expectedCols && report.expectedCols.length > 0 && (
            <div className="text-[10px] mt-1.5 flex items-start flex-wrap gap-1" style={{ color: '#685B72' }}>
              <span className="opacity-70 uppercase tracking-widest font-semibold mt-0.5">Expected columns:</span>
              {report.expectedCols.map(c => (
                <span key={c} className="px-1.5 py-0.5 rounded-md font-mono text-[10px]" style={{ background: '#F1EDF5', color: '#685B72' }}>{c}</span>
              ))}
            </div>
          )}
          {report.endpoints && report.endpoints.length > 1 && (
            <div className="text-[10px] mt-1.5 flex items-center flex-wrap gap-1.5" style={{ color: '#685B72' }}>
              <span className="opacity-60">Feeds:</span>
              {report.endpoints.map(ep => (
                <span key={ep.url} className="px-1.5 py-0.5 rounded-full font-semibold" style={{ background: `${tone}18`, color: tone }}>
                  {ep.label}
                </span>
              ))}
            </div>
          )}
          {w?.hint && (
            <div className="text-[11px] mt-1" style={{ color: '#685B72' }}>
              <span className="font-semibold">Why:</span> {w.hint}
            </div>
          )}
          {busy && progress && (
            <div className="text-[11px] mt-2 font-mono flex items-center gap-1 text-[#685B72]">
              <Loader2 size={11} className="animate-spin" /> Uploading to {progress}…
            </div>
          )}
          {result && (
            <div className="mt-2 space-y-0.5" data-testid={`import-result-${report.id}`}>
              {(result.breakdown || []).map((b, i) => (
                <div key={i} className={`text-[11px] font-mono flex items-start gap-1 ${b.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {b.ok ? <CheckCircle2 size={12} className="mt-0.5 shrink-0" /> : <AlertCircle size={12} className="mt-0.5 shrink-0" />}
                  <span>{b.label}: {b.ok ? `${(b.rows || 0).toLocaleString()} rows` : b.msg}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="rounded-xl p-2.5 shrink-0" style={{ background: `${tone}22` }}>
          {busy ? <Loader2 size={16} className="animate-spin" style={{ color: tone }} /> : <UploadCloud size={16} style={{ color: tone }} />}
        </div>
      </div>
      <input type="file" accept={report.accept} className="hidden" onChange={submit} disabled={busy || disabled} data-testid={`import-input-${report.id}`} />
    </label>
  );
}


function ImportSection({ section, canAccess, onDone, statsFetch, lastRefreshed }) {
  const Icon = section.icon;
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!canAccess || !statsFetch) return;
    statsFetch().then(setStatus).catch(() => {});
  }, [canAccess, statsFetch, lastRefreshed]);

  return (
    <Card
      data-testid={`import-section-${section.id}`}
      className={`rounded-2xl border p-5 lg:p-6 relative ${!canAccess ? 'opacity-60' : ''}`}
      style={{ background: 'white', borderColor: '#E8E5EC' }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="rounded-xl p-2.5 shrink-0" style={{ background: `${section.tone}18` }}>
            <Icon size={18} strokeWidth={1.75} style={{ color: section.tone }} />
          </div>
          <div className="min-w-0">
            <h3 className="font-serif text-xl leading-none" style={{ color: '#2D2833' }}>{section.title}</h3>
            <p className="text-[12px] mt-1.5" style={{ color: '#685B72' }}>{section.subtitle}</p>
          </div>
        </div>
        {status?.last && (
          <div className="text-[11px] flex items-center gap-1.5" style={{ color: '#839E97' }}>
            <CheckCircle2 size={13} /> Last: {fmtDateTime(status.last)}
          </div>
        )}
      </div>

      {canAccess && !section.disabled && (
        <div className={`grid gap-3 ${section.reports.length > 1 ? 'md:grid-cols-2' : 'grid-cols-1'}`}>
          {section.reports.map(r => (
            <ReportDropCard key={r.id} report={r} tone={section.tone} autoRefresh={section.autoRefresh} onDone={onDone} />
          ))}
        </div>
      )}
      {!canAccess && (
        <div className="text-[12px] rounded-lg px-3 py-2" style={{ background: '#FAF8F4', color: '#685B72' }}>
          {section.audience === 'super' ? 'Restricted to Super User.' : 'Restricted access — contact your admin.'}
        </div>
      )}
      {section.disabled && (
        <div className="text-[12px] rounded-lg px-3 py-2" style={{ background: '#FAF8F4', color: '#685B72' }}>
          This module isn't live yet — we'll wire it up in the next iteration.
        </div>
      )}
    </Card>
  );
}

// ─── One-shot Ops Hub V6 legacy-Excel importer — REMOVED post-launch ───
// (Deleted iter 53: no longer needed once production has finished its
// initial data migration. The destructive endpoint sat idle and posed a
// risk; strip both the FE component and BE route.)

// ─── iter 73: Duplicate audit panel ───────────────────────────────────
// Fetches /api/duplicates/audit on mount + on manual refresh, groups
// candidate merges by kind, and offers a per-group Merge / Skip / Ignore
// action. Non-blocking — sits above the Sections so the user sees the
// dedupe state before doing any new uploads.
function DuplicateAuditPanel() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // 'clients' | 'inquiries' | 'clinicians'
  const canRun = ['super_user', 'admin', 'hr_payroll'].includes(user?.role);

  const runAudit = useCallback(async () => {
    if (!canRun) return;
    setLoading(true);
    try {
      const r = await api.get('/duplicates/audit');
      setReport(r.data);
    } catch (e) {
      log.error('Duplicate audit failed', e);
      toast.error(e?.response?.data?.detail || 'Duplicate audit failed');
    } finally { setLoading(false); }
  }, [canRun]);
  useEffect(() => { if (canRun) runAudit(); }, [canRun, runAudit]);

  if (!canRun) return null;
  const counts = report?.counts || {};
  const total = (counts.clients || 0) + (counts.inquiries || 0) + (counts.clinicians || 0);
  const groups = report?.groups || {};

  const onIgnore = async (kind, ids) => {
    try {
      await api.post('/duplicates/ignore', { kind, ids });
      toast.success('Recorded — this pair will be ignored in future audits');
      runAudit();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to record'); }
  };
  const onMerge = async (kind, ids, target) => {
    // Merge = keep `target`, delete the others. Per user directive
    // ("new data wins"), the caller decides which record is authoritative;
    // /duplicates/merge soft-deletes clients/inquiries and hard-deletes
    // clinicians.
    try {
      const others = ids.filter(x => x !== target);
      await api.post('/duplicates/merge', { kind, keep: target, drop: others });
      toast.success(`Merged ${others.length} into ${target}`);
      runAudit();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Merge failed'); }
  };

  return (
    <Card className="rounded-2xl border p-5 mb-6" style={{ background: total > 0 ? '#B08A3A' : '#F1FBF3', borderColor: total > 0 ? '#B08A3A66' : '#B3D6C1' }} data-testid="duplicate-audit-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <AlertCircle size={18} style={{ color: total > 0 ? '#B08A3A' : '#839E97' }} />
          <div>
            <div className="font-semibold text-sm" style={{ color: '#2D2833' }}>
              Duplicate Audit · fuzzy match ≥ 85%
            </div>
            <div className="text-[11px]" style={{ color: '#685B72' }}>
              {total === 0 ? 'No candidates — every record has a clean natural key.'
                : `${total} group${total === 1 ? '' : 's'} of near-duplicates flagged across clients, inquiries & clinicians.`}
            </div>
          </div>
        </div>
        <Button onClick={runAudit} disabled={loading} variant="outline" size="sm" className="rounded-full text-xs" data-testid="duplicate-audit-refresh">
          {loading ? <><Loader2 size={12} className="mr-1 animate-spin" /> Scanning</> : <><RefreshCcw size={12} className="mr-1" /> Re-scan</>}
        </Button>
      </div>

      {total > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {['clients', 'inquiries', 'clinicians'].map(k => counts[k] > 0 && (
            <button
              key={k}
              onClick={() => setExpanded(expanded === k ? null : k)}
              className={`text-xs px-3 py-1.5 rounded-full font-semibold border ${expanded === k ? 'bg-[#2D2833] text-white' : 'bg-white text-[#2D2833] hover:bg-[#F7EFDA]'}`}
              style={{ borderColor: '#E8E5EC' }}
              data-testid={`duplicate-tab-${k}`}
            >
              {k.charAt(0).toUpperCase() + k.slice(1)} · {counts[k]}
            </button>
          ))}
        </div>
      )}

      {expanded && (groups[expanded] || []).length > 0 && (
        <div className="mt-4 space-y-3 max-h-[420px] overflow-y-auto pr-2">
          {(groups[expanded] || []).slice(0, 50).map((g, i) => {
            const idField = expanded === 'clients' ? 'client_id' : expanded === 'inquiries' ? 'inquiry_id' : 'clinician_id';
            const ids = g.records.map(r => r[idField]).filter(Boolean);
            const nameFor = (r) => expanded === 'clinicians'
                ? `${r.first_name || ''} ${r.last_name || ''}`.trim()
                : r.client_name || r.form_submitter || '—';
            return (
              <div key={`${expanded}-${i}`} className="rounded-xl border p-3 bg-white" style={{ borderColor: '#E8E5EC' }} data-testid={`duplicate-group-${expanded}-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: '#B08A3A' }}>
                    {g.reason.replace('_', ' ')} · {g.value?.slice(0, 40) || '—'}
                  </div>
                  <button onClick={() => onIgnore(expanded, ids)} className="text-[11px] text-[#685B72] underline underline-offset-2 hover:text-[#2D2833]" data-testid={`duplicate-ignore-${expanded}-${i}`}>Ignore forever</button>
                </div>
                <div className="space-y-1.5">
                  {g.records.map((r) => (
                    <div key={r[idField]} className="flex items-center justify-between text-[12px] gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate" style={{ color: '#2D2833' }}>{nameFor(r)}</div>
                        <div className="text-[10px] font-mono truncate" style={{ color: '#685B72' }}>
                          {r[idField]}{r.email ? ` · ${r.email}` : ''}{r.phone ? ` · ${r.phone}` : ''}{r.clinic ? ` · ${r.clinic}` : ''}
                        </div>
                      </div>
                      <button
                        onClick={() => onMerge(expanded, ids, r[idField])}
                        className="text-[11px] px-2 py-1 rounded border font-semibold hover:bg-[#F7EFDA]"
                        style={{ borderColor: '#B08A3A', color: '#B08A3A' }}
                        data-testid={`duplicate-keep-${expanded}-${r[idField]}`}
                      >
                        Keep this
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {(groups[expanded] || []).length > 50 && (
            <div className="text-[11px] text-center pt-2" style={{ color: '#685B72' }}>
              Showing first 50 of {(groups[expanded] || []).length} groups.
            </div>
          )}
        </div>
      )}
    </Card>
  );
}


// ─── iter 82: Legacy `_id` healer ─────────────────────────────────────
// ─── Audit response (2026-02-03): Pre-flight + Phase 2 migration panels ──
// UI-triggered runbook so nobody needs container shell access.

function PreflightPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const r = await api.get('/admin/migrations/preflight');
      setResult(r.data);
    } catch (e) {
      log.error('preflight failed', e);
      toast.error(e?.response?.data?.detail || 'Preflight failed');
    } finally { setLoading(false); }
  };

  const overallBg = { green: '#F1FBF3', yellow: '#FEF6E4', red: '#FCE9E9' }[result?.overall] || '#FFFFFF';
  const overallBorder = { green: '#B3D6C1', yellow: '#F0C878', red: '#E4A5A5' }[result?.overall] || '#E8E5EC';
  const dotColor = (s) => ({ green: '#4B7A5C', yellow: '#B08A3A', red: '#8A3A3A' }[s] || '#685B72');

  return (
    <Card className="rounded-2xl border p-5 mb-6" style={{ background: overallBg, borderColor: overallBorder }} data-testid="preflight-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <ShieldAlert size={18} className="mt-0.5" style={{ color: '#685B72' }} />
          <div>
            <div className="font-semibold text-sm" style={{ color: '#2D2833' }}>
              Pre-flight check
              {result?.overall && (
                <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest" style={{ color: dotColor(result.overall) }}>
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: dotColor(result.overall) }} />
                  {result.overall}
                </span>
              )}
            </div>
            <div className="text-[11px] mt-1 leading-relaxed" style={{ color: '#685B72', maxWidth: 640 }}>
              Runs 6 checks (fresh backup, disk headroom, openapi builds, rate-book invariant, scheduler leader, FRONTEND_URL). Green = safe to migrate or deploy. Red = fix something before proceeding.
            </div>
          </div>
        </div>
        <Button onClick={run} disabled={loading} className="rounded-full text-white font-semibold" style={{ background: '#685B72', border: 'none' }} data-testid="preflight-run-btn">
          {loading ? <><Loader2 size={14} className="animate-spin mr-2" /> Running…</> : 'Run pre-flight'}
        </Button>
      </div>
      {result && (
        <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-2 gap-2" style={{ borderColor: overallBorder }} data-testid="preflight-result">
          {Object.entries(result.checks || {}).map(([k, v]) => (
            <div key={k} className="flex items-start gap-2 rounded-lg bg-white p-3 border" style={{ borderColor: '#E8E5EC' }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: dotColor(v.status), marginTop: 6 }} />
              <div className="text-xs">
                <div className="font-semibold" style={{ color: '#2D2833' }}>{k.replaceAll('_', ' ')}</div>
                <div className="text-[11px]" style={{ color: '#685B72' }}>{v.detail}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


function Phase2MigrationPanel() {
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(null);
  const [result, setResult] = useState(null);

  const runDryRun = async () => {
    if (loading) return;
    setLoading(true); setMode('dry-run');
    try {
      const r = await api.post('/admin/migrations/phase2/dry-run');
      setResult(r.data);
      toast.success('Dry-run complete — nothing was changed.');
    } catch (e) {
      log.error('dry-run failed', e);
      toast.error(e?.response?.data?.detail || 'Dry-run failed');
    } finally { setLoading(false); }
  };

  const runApply = async () => {
    if (loading) return;
    const token = window.prompt(
      'Type APPLY_PHASE_2 to confirm the migration will WRITE to the database.\n\n'
      + 'This is idempotent (safe to re-run) and every change is journaled. '
      + 'Make sure you have a fresh backup before proceeding.'
    );
    if (token !== 'APPLY_PHASE_2') {
      if (token !== null) toast.error('Wrong confirm token — apply cancelled.');
      return;
    }
    setLoading(true); setMode('apply');
    try {
      const r = await api.post('/admin/migrations/phase2/apply', { confirm: 'APPLY_PHASE_2' });
      setResult(r.data);
      toast.success('Phase 2 migration applied.');
    } catch (e) {
      log.error('apply failed', e);
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally { setLoading(false); }
  };

  const stepsToShow = result?.steps ? Object.entries(result.steps) : [];

  return (
    <Card className="rounded-2xl border p-5 mb-6" style={{ background: '#FBF7FF', borderColor: '#C7B3E0' }} data-testid="phase2-migration-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Database size={18} className="mt-0.5" style={{ color: '#685B72' }} />
          <div>
            <div className="font-semibold text-sm" style={{ color: '#2D2833' }}>
              Phase 2 data cleanup migration
            </div>
            <div className="text-[11px] mt-1 leading-relaxed" style={{ color: '#685B72', maxWidth: 640 }}>
              Rewrites legacy <code>Inactive</code> stages to the suffixed prefix, converts non-ISO referral dates, populates <code>referral_date_ts</code>, trims whitespace, backfills <code>converted_client_id</code> on legacy converted inquiries. Idempotent — safe to run twice. Run dry-run first, then apply.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={runDryRun} disabled={loading} variant="outline" className="rounded-full text-xs font-semibold" data-testid="phase2-dry-run-btn">
            {loading && mode === 'dry-run' ? <><Loader2 size={14} className="animate-spin mr-2" /> Running…</> : 'Dry-run'}
          </Button>
          <Button onClick={runApply} disabled={loading} className="rounded-full text-white text-xs font-semibold" style={{ background: '#8A3A3A', border: 'none' }} data-testid="phase2-apply-btn">
            {loading && mode === 'apply' ? <><Loader2 size={14} className="animate-spin mr-2" /> Applying…</> : 'Apply'}
          </Button>
        </div>
      </div>
      {result && (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: '#D7C4EE' }} data-testid="phase2-result">
          <div className="text-[10px] uppercase tracking-widest font-semibold mb-2" style={{ color: result.mode === 'apply' ? '#8A3A3A' : '#4B7A5C' }}>
            {result.mode} · {result.elapsed_seconds}s
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {stepsToShow.map(([k, v]) => (
              <div key={k} className="rounded-lg bg-white p-3 border text-xs" style={{ borderColor: '#E8E5EC' }}>
                <div className="font-semibold" style={{ color: '#2D2833' }}>{k}</div>
                <pre className="mt-1 text-[10px] whitespace-pre-wrap" style={{ color: '#685B72' }}>
{JSON.stringify(v, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}



// Pre-iter73 BI rows carry random ObjectId `_id`s; the current ingest
// path writes natural-key sha1 ids. Re-uploading SP reports after the
// switch inserted a fresh row alongside every legacy one — doubling
// production BI numbers. This panel runs the one-shot idempotent
// migration that rewrites every legacy row to its natural-key id (and
// dedupes on collision). Safe to re-run — reports `touched: 0` once
// the DB is clean.
function LegacyIdHealerPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (loading) return;
    if (!window.confirm(
      'Rewrite every legacy BI row (_id: ObjectId) to its natural-key id? '
      + 'Duplicates collapse automatically. Safe to run — recomputes BI '
      + 'snapshot at the end. This can take 30-60s on production.'
    )) return;
    setLoading(true);
    try {
      const r = await api.post('/admin/normalize-bi-ids');
      setResult(r.data);
      const touched = r.data?.touched || 0;
      if (touched === 0) toast.success('DB is already clean — no rows touched.');
      else toast.success(`Rewrote ${touched.toLocaleString()} legacy rows to natural-key ids. BI snapshot refreshed.`);
    } catch (e) {
      log.error('normalize-bi-ids failed', e);
      toast.error(e?.response?.data?.detail || 'Migration failed');
    } finally { setLoading(false); }
  };

  const collDelta = (name) => {
    const r = result?.report?.[name];
    if (!r) return null;
    return `${r.total_before.toLocaleString()} → ${r.total_after.toLocaleString()} (net ${r.net_delta >= 0 ? '+' : ''}${r.net_delta.toLocaleString()})`;
  };

  return (
    <Card className="rounded-2xl border p-5 mb-6" style={{ background: '#FBF7FF', borderColor: '#C7B3E0' }} data-testid="legacy-id-healer-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <AlertCircle size={18} className="mt-0.5" style={{ color: '#685B72' }} />
          <div>
            <div className="font-semibold text-sm" style={{ color: '#2D2833' }}>
              Legacy BI Row-ID Healer · one-shot migration
            </div>
            <div className="text-[11px] mt-1 leading-relaxed" style={{ color: '#685B72', maxWidth: 640 }}>
              If Executive BI numbers ever appear <b>doubled</b> after a re-upload, run this once.
              It rewrites every pre-iter73 row to its natural-key id (client + DOS + code), collapses
              duplicates automatically, and refreshes the BI snapshot. Idempotent — safe to re-run.
            </div>
          </div>
        </div>
        <Button
          onClick={run}
          disabled={loading}
          className="rounded-full text-white font-semibold"
          style={{ background: '#685B72', border: 'none' }}
          data-testid="normalize-bi-ids-btn"
        >
          {loading ? <><Loader2 size={14} className="animate-spin mr-2" /> Running…</> : 'Normalize Legacy IDs'}
        </Button>
      </div>
      {result && (
        <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-3" style={{ borderColor: '#D7C4EE' }} data-testid="normalize-bi-ids-result">
          {['bi_appt_status', 'bi_attendance', 'bi_claims'].map(name => (
            <div key={name} className="rounded-xl bg-white border p-3" style={{ borderColor: '#E8DFF5' }}>
              <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#685B72' }}>{name}</div>
              <div className="text-sm font-semibold mt-1" style={{ color: '#2D2833' }}>{collDelta(name) || '—'}</div>
              <div className="text-[11px] mt-0.5" style={{ color: '#685B72' }}>
                {result.report?.[name]?.rewritten?.toLocaleString() || 0} rewritten
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


function BiSpDataWipePanel({ onDone }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const run = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const r = await api.post('/admin/wipe-bi-sp-data');
      setResult(r.data);
      setConfirmOpen(false);
      toast.success(`Wiped ${(r.data?.total_deleted || 0).toLocaleString()} rows across 7 SP collections. Re-upload your reports below to rebuild BI.`);
      onDone?.();
    } catch (e) {
      log.error('wipe-bi-sp-data failed', e);
      toast.error(e?.response?.data?.detail || 'Wipe failed');
    } finally { setLoading(false); }
  };

  const SP_COLLS = ['bi_appt_status', 'bi_attendance', 'bi_claims', 'bi_income', 'bi_payer_alloc', 'bi_snapshot', 'bi_uploads'];

  return (
    <Card
      className="rounded-2xl border p-5 mb-6"
      style={{ background: '#F5EAEA', borderColor: '#8A3A3A' }}
      data-testid="bi-sp-wipe-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <AlertCircle size={18} className="mt-0.5" style={{ color: '#8A3A3A' }} />
          <div>
            <div className="font-semibold text-sm" style={{ color: '#2D2833' }}>
              Hard Reset · Wipe SimplePractice BI Data
            </div>
            <div className="text-[11px] mt-1 leading-relaxed" style={{ color: '#685B72', maxWidth: 640 }}>
              Nuclear option when the Legacy ID Healer isn&apos;t enough. Empties every
              SP-derived BI collection so you can re-import fresh. <b>Client Pipeline,
              Inquiries, Clinicians, Clinics, Users, Setup, Rates, and Availability
              are NOT touched.</b> After running, re-upload the Appointment Status Report
              (and any other SP reports) via the sections below.
            </div>
          </div>
        </div>
        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={loading}
          className="rounded-full text-white font-semibold"
          style={{ background: '#F5EAEA', border: 'none' }}
          data-testid="wipe-bi-sp-data-btn"
        >
          {loading ? <><Loader2 size={14} className="animate-spin mr-2" /> Wiping…</> : 'Wipe SP BI Data'}
        </Button>
      </div>
      <ConfirmDestructive
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        tone="danger"
        title="Wipe SimplePractice BI data"
        description={
          <>
            This empties <b>bi_appt_status, bi_attendance, bi_claims, bi_income, bi_payer_alloc, bi_snapshot, bi_uploads</b>.
            Client Pipeline, Inquiries, Clinicians, Clinics, Users, Setup, Rates, and Availability
            grids are <b>NOT</b> touched. You will need to re-upload your SP reports below to rebuild BI.
          </>
        }
        confirmPhrase="WIPE-SP-DATA"
        confirmLabel="Wipe SP BI Data"
        running={loading}
        onConfirm={run}
        testId="confirm-wipe-sp-data"
      />
      {result && (
        <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-2" style={{ borderColor: '#8A3A3A' }} data-testid="wipe-bi-sp-data-result">
          {SP_COLLS.map(name => {
            const row = result.report?.[name];
            if (!row) return null;
            return (
              <div key={name} className="rounded-xl bg-white border p-3" style={{ borderColor: '#8A3A3A' }}>
                <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#8A3A3A' }}>{name}</div>
                <div className="text-sm font-semibold mt-1" style={{ color: '#2D2833' }}>
                  {row.before.toLocaleString()} → 0
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: '#685B72' }}>
                  {row.deleted.toLocaleString()} deleted
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}


// ─── Database Backup & Restore panel (super_user only, iter 89) ─────────
// Lists existing on-disk snapshots, lets the operator take a new snapshot,
// download any archive, remove old ones, and (rarely) upload an older
// archive to fully restore the DB. Every action goes through /api/admin/
// backup which is audit-logged and rate-limited on the backend.
function BackupPanel() {
  const [backups, setBackups] = useState([]);
  const [dir, setDir] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [restoringUpload, setRestoringUpload] = useState(false);
  const [backupModalOpen, setBackupModalOpen] = useState(false);
  // ── Google Drive nightly-backup wiring (iter 92) ──────────────────────
  const [drive, setDrive] = useState(null);
  const [driveBusy, setDriveBusy] = useState(false);
  const [driveTesting, setDriveTesting] = useState(false);

  const loadDrive = async () => {
    try {
      const r = await api.get('/admin/backup/drive/status');
      setDrive(r.data);
    } catch { setDrive(null); }
  };

  const connectDrive = async () => {
    if (driveBusy) return;
    setDriveBusy(true);
    try {
      const r = await api.get('/admin/backup/drive/connect');
      // Open Google's consent screen in a new tab; the callback URL brings
      // the user back to /imports?drive_connected=1 when done.
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start Google sign-in');
      setDriveBusy(false);
    }
  };

  const disconnectDrive = async () => {
    if (!window.confirm('Disconnect Google Drive? Nightly backups will still run locally, but will no longer upload off-server.')) return;
    try {
      await api.post('/admin/backup/drive/disconnect');
      toast.success('Google Drive disconnected');
      loadDrive();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Disconnect failed');
    }
  };

  const testDriveRun = async () => {
    if (driveTesting) return;
    setDriveTesting(true);
    try {
      const r = await api.post('/admin/backup/drive/test');
      if (r.data?.drive?.uploaded) {
        toast.success(`Test backup uploaded · ${(r.data.total_rows || 0).toLocaleString()} rows · file in Drive folder`);
      } else if (r.data?.ok) {
        toast.warning('Local snapshot ok, but Drive upload skipped: ' + (r.data.drive?.reason || r.data.drive?.error || 'unknown'));
      } else {
        toast.error('Backup failed: ' + (r.data?.error || 'unknown error'));
      }
      load(); loadDrive();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test run failed');
    } finally { setDriveTesting(false); }
  };

  // Iter 130: pods are ephemeral so the local /app/backups folder gets
  // wiped on redeploy — leaving old rows in `backups_meta` orphaned from
  // the actual .tar.gz files (which are still safe on Drive). Reconcile
  // scans the Drive folder and backfills the `drive_file_id` +
  // `drive_web_view_link` fields on every matching row so the UI can
  // show "ON DRIVE" and offer download/restore via Drive.
  const [driveReconciling, setDriveReconciling] = useState(false);
  const reconcileDrive = async () => {
    if (driveReconciling) return;
    setDriveReconciling(true);
    try {
      const r = await api.post('/admin/backup/drive/reconcile');
      toast.success(`Reconciled · scanned ${r.data.drive_files_scanned || 0} files on Drive · linked ${r.data.rows_updated || 0} backups.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reconcile failed');
    } finally { setDriveReconciling(false); }
  };

  const load = async () => {
    setLoadingList(true);
    try {
      const r = await api.get('/admin/backup/list');
      setBackups(r.data.backups || []);
      setDir(r.data.backup_dir || '');
    } catch (e) {
      log.error('backup/list failed', e);
    } finally { setLoadingList(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadDrive(); }, []);
  // Show a success/error toast when the OAuth callback bounces back with
  // ?drive_connected=1 (or =error). One-shot; strips the query param.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const flag = q.get('drive_connected');
    if (!flag) return;
    if (flag === '1') toast.success('Google Drive connected — nightly backups will upload automatically.');
    else {
      const reason = q.get('reason') || flag;
      const detail = q.get('detail') || '';
      const HINTS = {
        no_refresh_token: 'Google didn\'t return a refresh token. Revoke Thrive access at https://myaccount.google.com/permissions and click Connect again.',
        missing_scope: detail
          ? `Google didn\'t return a Drive scope. ${detail}. Please redo the consent and make sure the Drive access checkbox is ticked.`
          : 'Please tick the Google Drive checkbox on the consent screen and try again.',
        missing_code_or_state: 'The sign-in was interrupted. Please click Connect again.',
        unknown_state: 'This sign-in link expired. Please click Connect again.',
        access_denied: 'You cancelled the Google consent screen. Click Connect again if you\'d like to grant access.',
        exception: detail
          ? `Something went wrong on the server: ${detail}`
          : 'Something went wrong on the server. Check the browser console / support for details.',
      };
      const hint = HINTS[reason] || `Reason: ${reason}${detail ? ' — ' + detail : ''}`;
      toast.error(`Google Drive connect failed. ${hint}`, { duration: 15000 });
    }
    q.delete('drive_connected'); q.delete('reason'); q.delete('detail');
    const newUrl = window.location.pathname + (q.toString() ? '?' + q.toString() : '');
    window.history.replaceState({}, '', newUrl);
    loadDrive();
  }, []);

  const create = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const r = await api.post('/admin/backup/create');
      toast.success(`Backup created · ${(r.data.total_rows || 0).toLocaleString()} rows · ${r.data.size_human}`);
      load();
    } catch (e) {
      log.error('backup/create failed', e);
      toast.error(e?.response?.data?.detail || 'Backup failed. Try again in a moment.');
    } finally { setCreating(false); }
  };

  const download = async (bid, useDrive = false) => {
    try {
      const path = useDrive
        ? `/admin/backup/drive/download/${bid}`
        : `/admin/backup/download/${bid}`;
      const r = await api.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${bid}.tar.gz`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      log.error('backup/download failed', e);
      toast.error('Download failed');
    }
  };

  const remove = async (bid) => {
    if (!window.confirm(`Delete backup ${bid}? This removes it from disk permanently.`)) return;
    try {
      await api.delete(`/admin/backup/${bid}`);
      toast.success('Backup deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const restore = async (bid, useDrive = false) => {
    const c = window.prompt(
      `RESTORE from ${bid} — this REPLACES every collection in the database with the contents of the archive.\n\n` +
      (useDrive ? `(Restoring from the Google Drive copy — the local file is no longer on this server.)\n\n` : '') +
      `Type REPLACE_ALL_DATA to confirm.`
    );
    if (c !== 'REPLACE_ALL_DATA') {
      if (c !== null) toast.error('Confirmation string did not match — aborted.');
      return;
    }
    setRestoringId(bid);
    try {
      if (useDrive) {
        // No direct "restore from Drive" endpoint — fetch the archive
        // from Drive first, then hand it to /upload-and-restore. This
        // keeps a single restore code path (which is well-tested) even
        // when the local file has been pruned.
        const dl = await api.get(`/admin/backup/drive/download/${bid}`, { responseType: 'blob' });
        const fd = new FormData();
        fd.append('file', new File([dl.data], `${bid}.tar.gz`, { type: 'application/gzip' }));
        const r = await api.post('/admin/backup/upload-and-restore?confirm=REPLACE_ALL_DATA', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success(`Restored from Drive · ${(r.data.total_rows || 0).toLocaleString()} rows across ${Object.keys(r.data.row_counts || {}).length} collections`);
      } else {
        const r = await api.post(`/admin/backup/restore/${bid}?confirm=REPLACE_ALL_DATA`);
        toast.success(`Restored · ${(r.data.total_rows || 0).toLocaleString()} rows across ${Object.keys(r.data.row_counts || {}).length} collections`);
      }
    } catch (e) {
      log.error('backup/restore failed', e);
      toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally { setRestoringId(null); }
  };

  const uploadRestore = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const c = window.prompt(
      `Upload-and-restore from "${file.name}" — this REPLACES every collection in the database.\n\n` +
      `Type REPLACE_ALL_DATA to confirm.`
    );
    if (c !== 'REPLACE_ALL_DATA') {
      if (c !== null) toast.error('Confirmation string did not match — aborted.');
      return;
    }
    setRestoringUpload(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/admin/backup/upload-and-restore?confirm=REPLACE_ALL_DATA', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Restored · ${(r.data.total_rows || 0).toLocaleString()} rows`);
    } catch (err) {
      log.error('upload-and-restore failed', err);
      toast.error(err?.response?.data?.detail || 'Restore failed');
    } finally { setRestoringUpload(false); }
  };

  return (
    <Card
      className="rounded-2xl border p-5 mb-6"
      style={{ background: '#F5F8F7', borderColor: '#839E97' }}
      data-testid="backup-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Database size={18} className="mt-0.5" style={{ color: '#839E97' }} />
          <div>
            <div className="font-semibold text-sm" style={{ color: '#2D2833' }}>
              Database Backup & Restore
            </div>
            <div className="text-[11px] mt-1 leading-relaxed" style={{ color: '#839E97', maxWidth: 640 }}>
              Snapshot every operational + BI collection into a single downloadable archive.
              Download the file and keep a copy off-server. Restore replaces the current DB with
              the archive — use only in a disaster-recovery scenario. Actions are super-user only,
              rate-limited, and written to the audit log.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label
            className="rounded-full text-white font-semibold text-[13px] px-4 py-2 cursor-pointer inline-flex items-center"
            style={{ background: '#685B72', border: 'none', opacity: restoringUpload ? 0.6 : 1 }}
            data-testid="backup-upload-restore-btn"
          >
            {restoringUpload
              ? <><Loader2 size={14} className="animate-spin mr-2" /> Restoring…</>
              : <><UploadCloud size={14} className="mr-2" /> Upload archive & restore</>}
            <input type="file" accept=".tar.gz,application/gzip" className="hidden" onChange={uploadRestore} disabled={restoringUpload} />
          </label>
          <Button
            onClick={create}
            disabled={creating}
            className="rounded-full text-white font-semibold"
            style={{ background: '#839E97', border: 'none' }}
            data-testid="backup-create-btn"
          >
            {creating ? <><Loader2 size={14} className="animate-spin mr-2" /> Creating…</> : <><Database size={14} className="mr-2" /> Create Backup</>}
          </Button>
        </div>
      </div>

      {/* ── Google Drive nightly backup subsection (iter 92) ─────── */}
      <div className="mt-4 pt-4 border-t rounded-xl bg-white p-4" style={{ borderColor: '#CFE0DB' }} data-testid="drive-backup-section">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            {drive?.connected
              ? <Cloud size={18} className="mt-0.5" style={{ color: '#839E97' }} />
              : <CloudOff size={18} className="mt-0.5" style={{ color: '#8A3A3A' }} />}
            <div>
              <div className="font-semibold text-sm flex items-center gap-2" style={{ color: '#2D2833' }}>
                Nightly Backup → Google Drive
                {drive?.connected
                  ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#E8F1EE', color: '#839E97' }} data-testid="drive-connected-badge">CONNECTED</span>
                  : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#F5EAEA', color: '#8A3A3A' }} data-testid="drive-disconnected-badge">NOT CONNECTED</span>}
              </div>
              <div className="text-[11px] mt-1 leading-relaxed" style={{ color: '#839E97', maxWidth: 640 }}>
                {drive?.connected ? (
                  <>
                    Runs every night at <b>{String(drive.nightly_hour).padStart(2, '0')}:00 {drive.nightly_tz}</b>.
                    Backups upload to your <a href={drive.folder_url} target="_blank" rel="noreferrer" className="underline" style={{ color: '#839E97' }}>Google Drive folder <ExternalLink size={10} className="inline" /></a>.
                    Keeps <b>{drive.drive_retention_days} days</b> on Drive · <b>{drive.local_retention_days} days</b> on this server.
                    {drive.connected_by && <> Connected by <b>{drive.connected_by}</b> on {fmtDateTime(drive.connected_at)}.</>}
                  </>
                ) : (
                  <>
                    Turn on off-server backup so a lost pod or corrupted DB never means lost data.
                    Sign in once with a <b>@thrivespeech.net</b> Google account — after that, backups upload every night automatically.
                    Scheduled: <b>{String(drive?.nightly_hour ?? 2).padStart(2, '0')}:00 {drive?.nightly_tz || 'America/Chicago'}</b>.
                  </>
                )}
              </div>
              {drive?.last_run && (
                <div className="text-[10px] mt-1.5" style={{ color: '#839E97' }} data-testid="drive-last-run">
                  Last run: {fmtDateTime(drive.last_run.at)} · {(drive.last_run.total_rows || 0).toLocaleString()} rows ·{' '}
                  {drive.last_run.drive?.uploaded
                    ? <span style={{ color: '#839E97' }}>uploaded to Drive ✓</span>
                    : <span style={{ color: '#8A3A3A' }}>Drive upload skipped ({drive.last_run.drive?.reason || drive.last_run.drive?.error || 'unknown'})</span>}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {drive?.connected ? (
              <>
                <Button
                  onClick={testDriveRun}
                  disabled={driveTesting}
                  size="sm"
                  variant="outline"
                  className="rounded-full text-[12px] h-8"
                  data-testid="drive-test-btn"
                >
                  {driveTesting
                    ? <><Loader2 size={12} className="animate-spin mr-1" /> Running…</>
                    : <><Zap size={12} className="mr-1" /> Test Run Now</>}
                </Button>
                <Button
                  onClick={reconcileDrive}
                  disabled={driveReconciling}
                  size="sm"
                  variant="outline"
                  className="rounded-full text-[12px] h-8"
                  data-testid="drive-reconcile-btn"
                  title="Scan Google Drive and re-link any backups whose local file was pruned. Safe to run anytime."
                >
                  {driveReconciling
                    ? <><Loader2 size={12} className="animate-spin mr-1" /> Reconciling…</>
                    : <><RefreshCcw size={12} className="mr-1" /> Reconcile</>}
                </Button>
                <Button
                  onClick={disconnectDrive}
                  size="sm"
                  variant="ghost"
                  className="rounded-full text-[12px] h-8"
                  data-testid="drive-disconnect-btn"
                >
                  Disconnect
                </Button>
              </>
            ) : (
              <Button
                onClick={connectDrive}
                disabled={driveBusy}
                className="rounded-full text-white font-semibold text-[13px]"
                style={{ background: '#685B72', border: 'none' }}
                data-testid="drive-connect-btn"
              >
                {driveBusy
                  ? <><Loader2 size={14} className="animate-spin mr-2" /> Redirecting…</>
                  : <><Cloud size={14} className="mr-2" /> Connect Google Drive</>}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t" style={{ borderColor: '#CFE0DB' }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#839E97' }}>
              Existing backups
            </div>
            {backups.length > 0 && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: '#E4EEE9', color: '#3B6F5D' }}>
                {backups.length}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {dir && (
              <div className="text-[10px]" style={{ color: '#839E97' }}>
                on server: <code style={{ background: '#EDF3F1', padding: '1px 6px', borderRadius: 4 }}>{dir}</code>
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setBackupModalOpen(true)}
              disabled={loadingList}
              className="rounded-full text-[12px] h-8"
              style={{ borderColor: '#839E97', color: '#3B6F5D' }}
              data-testid="backup-view-list-btn"
            >
              {loadingList
                ? <><Loader2 size={12} className="animate-spin mr-1.5" /> Loading…</>
                : <><Archive size={12} className="mr-1.5" /> View backups{backups.length > 0 ? ` (${backups.length})` : ''}</>}
            </Button>
          </div>
        </div>
        {!loadingList && backups.length === 0 && (
          <div className="text-[12px] py-4 text-center" style={{ color: '#839E97' }}>
            No backups yet. Click <b>Create Backup</b> to take the first snapshot.
          </div>
        )}
      </div>

      <Dialog open={backupModalOpen} onOpenChange={setBackupModalOpen}>
        <DialogContent
          className="max-w-4xl w-[95vw] max-h-[85vh] overflow-hidden flex flex-col p-0"
          data-testid="backup-list-modal"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b" style={{ borderColor: '#DBE7E3' }}>
            <DialogTitle className="flex items-center gap-2 text-[15px]" style={{ color: '#2D2833' }}>
              <Archive size={16} style={{ color: '#3B6F5D' }} />
              Backups
              {backups.length > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: '#E4EEE9', color: '#3B6F5D' }}>
                  {backups.length}
                </span>
              )}
              {dir && (
                <span className="ml-auto text-[10px] font-normal" style={{ color: '#839E97' }}>
                  on server: <code style={{ background: '#EDF3F1', padding: '1px 6px', borderRadius: 4 }}>{dir}</code>
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loadingList ? (
              <div className="text-[12px]" style={{ color: '#839E97' }}>Loading…</div>
            ) : backups.length === 0 ? (
              <div className="text-[12px] py-8 text-center" style={{ color: '#839E97' }}>
                No backups yet. Click <b>Create Backup</b> to take the first snapshot.
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="backup-list">
                {backups.map(b => {
                  // A backup is "recoverable" if the local file is present OR
                  // we have a Drive file id we can pull it back from. Only when
                  // BOTH are missing do we really lose the archive.
                  const onDrive = !!b.drive_file_id;
                  const recoverable = b.archive_present || onDrive;
                  const useDrive = !b.archive_present && onDrive;
                  return (
                  <div key={b.backup_id} className="rounded-xl bg-white border p-3 flex items-center justify-between gap-3 flex-wrap" style={{ borderColor: '#DBE7E3' }}>
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-[12px] font-semibold truncate" style={{ color: '#2D2833' }}>
                        {b.backup_id}
                        {b.archive_present && onDrive && (
                          <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#E4EEE9', color: '#3B6F5D' }} data-testid={`backup-pill-both-${b.backup_id}`}>
                            LOCAL + DRIVE
                          </span>
                        )}
                        {!b.archive_present && onDrive && (
                          <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ background: '#E8EDF5', color: '#3F5B8A' }} data-testid={`backup-pill-drive-${b.backup_id}`}>
                            <Cloud size={10} /> ON DRIVE
                          </span>
                        )}
                        {!b.archive_present && !onDrive && (
                          <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: '#F5EAEA', color: '#8A3A3A' }} data-testid={`backup-pill-missing-${b.backup_id}`}>
                            FILE MISSING
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] mt-0.5 flex flex-wrap gap-3" style={{ color: '#839E97' }}>
                        <span>{fmtDateTime(b.created_at)}</span>
                        <span>by {b.created_by}</span>
                        <span>{(b.total_rows || 0).toLocaleString()} rows</span>
                        <span>{b.size_human}</span>
                        <span>{(b.collections || []).length} collections</span>
                        {b.drive_web_view_link && (
                          <a href={b.drive_web_view_link} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5" style={{ color: '#3F5B8A' }} data-testid={`backup-drive-link-${b.backup_id}`}>
                            Open in Drive <ExternalLink size={10} />
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => download(b.backup_id, useDrive)}
                        disabled={!recoverable}
                        className="rounded-full text-[11px] h-8"
                        data-testid={`backup-download-${b.backup_id}`}
                      >
                        <Download size={12} className="mr-1" /> Download{useDrive ? ' (Drive)' : ''}
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => restore(b.backup_id, useDrive)}
                        disabled={!recoverable || restoringId === b.backup_id}
                        className="rounded-full text-[11px] h-8"
                        style={{ borderColor: '#8A3A3A', color: '#8A3A3A' }}
                        data-testid={`backup-restore-${b.backup_id}`}
                      >
                        {restoringId === b.backup_id
                          ? <><Loader2 size={12} className="animate-spin mr-1" /> Restoring…</>
                          : <><ShieldAlert size={12} className="mr-1" /> Restore{useDrive ? ' (Drive)' : ''}</>}
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => remove(b.backup_id)}
                        className="rounded-full text-[11px] h-8"
                        data-testid={`backup-delete-${b.backup_id}`}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


export default function ImportPage() {
  const { user } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [sp, setSp] = useState(null);
  const [perfMonths, setPerfMonths] = useState([]);
  const [biStatus, setBiStatus] = useState(null);

  const load = () => {
    api.get('/appointments/stats').then(r => setSp(r.data)).catch(() => {});
    api.get('/performance/months').then(r => setPerfMonths(r.data.combined || [])).catch(() => {});
    if (['super_user', 'admin', 'hr_payroll', 'data_import'].includes(user?.role)) api.get('/bi/status').then(r => setBiStatus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line

  const onDone = () => setRefreshKey(k => k + 1);

  const canAccess = (section) => {
    if (section.disabled) return true;
    // Data-Import service account can upload every recurring report but
    // has none of the super-user-only side panels (backup, migrations, wipe).
    if (section.audience === 'super') {
      return ['super_user', 'data_import'].includes(user?.role);
    }
    return ['super_user', 'admin', 'hr_payroll', 'data_import'].includes(user?.role);
  };

  const statsFetchFor = (id) => {
    if (id === 'unified') return () => api.get('/bi/status').then(r => ({ last: r.data?.last_refresh }));
    return null;
  };

  return (
    <div data-testid="import-page" style={{ background: '#FBF8F4', minHeight: 'calc(100vh - 4rem)', margin: '-1rem -1rem -2rem -1rem', padding: '1.5rem 2rem 2.5rem' }}>
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: '#B08A3A' }}>One place for every upload</div>
          <h1 className="font-serif mt-1.5 leading-none" style={{ color: '#685B72', fontSize: 'clamp(28px, 4vw, 40px)' }}>Data Import Center</h1>
          <p className="text-[13px] mt-2 max-w-3xl" style={{ color: '#685B72' }}>
            Every recurring report lives here with a strict time-window so you know exactly what to export. Uploads are validated by
            column signature — drop the wrong file and it's rejected with a helpful hint instead of silently corrupting data.
          </p>
        </div>
        <Button data-testid="import-refresh-btn" onClick={onDone} className="rounded-full h-10 px-4 border-0" style={{ background: '#685B72', color: '#FFFFFF' }}>
          <RefreshCcw size={13} className="mr-2" /> Refresh status
        </Button>
      </div>

      {/* Time-window primer card */}
      <Card className="rounded-2xl border p-5 mb-6" style={{ background: '#F7EFDA', borderColor: '#B08A3A' }} data-testid="import-time-window-primer">
        <div className="flex items-start gap-3">
          <Info size={18} className="shrink-0 mt-0.5" style={{ color: '#B08A3A' }} />
          <div className="grid md:grid-cols-2 gap-4 flex-1">
            <div>
              <div className="flex items-center gap-2 mb-1"><TimeWindowBadge window="full_year" /></div>
              <p className="text-[12px] leading-relaxed" style={{ color: '#2D2833' }}>
                <strong>Appointment identity never changes.</strong> An appointment is always the same
                <em> client + date + CPT</em>. What DOES change over time is the amount collected and the note status —
                sometimes months after the visit. We upsert by that natural key so re-uploads refresh the mutable fields
                without touching immutable ones. <strong>Upload YTD every time</strong> so late payments cascade across the whole year.
              </p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1"><TimeWindowBadge window="ninety_day" /></div>
              <p className="text-[12px] leading-relaxed" style={{ color: '#2D2833' }}>
                <strong>Zero-loss ingestion.</strong> Data outside the file's date range is preserved.
                If your file only covers Feb onwards, January stays intact. If your file re-covers a period,
                the system UPDATES existing rows in place — it never creates duplicates and never deletes history.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Smart normalization notice */}
      <Card className="rounded-2xl border p-4 mb-6 flex items-start gap-3" style={{ background: '#FBF9F5', borderColor: '#E8E5EC' }}>
        <Info size={16} className="shrink-0 mt-0.5" style={{ color: '#685B72' }} />
        <div className="text-[12px] leading-relaxed" style={{ color: '#2D2833' }}>
          <span className="font-semibold">On upload we also automatically:</span> strip credentials from clinician names
          (<em>"John Lever MSC PP"</em> → <em>"John Lever"</em>) to prevent duplicates,
          reassign Telehealth / missing offices to the clinician's majority physical location,
          validate the file's column signature, and smart-merge overlapping months (replace, not duplicate).
        </div>
      </Card>

      <DuplicateAuditPanel />

      {user?.role === 'super_user' && <PreflightPanel />}
      {user?.role === 'super_user' && <Phase2MigrationPanel />}
      {user?.role === 'super_user' && <LegacyIdHealerPanel />}
      {user?.role === 'super_user' && <BiSpDataWipePanel onDone={onDone} />}
      {user?.role === 'super_user' && <BackupPanel />}

      {/* Quick status pills */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: '#E8E5EC' }} data-testid="status-sp">
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#685B72' }}>SimplePractice</div>
          <div className="font-serif text-2xl mt-1" style={{ color: '#2D2833' }}>{sp?.total?.toLocaleString() || '—'}</div>
          <div className="text-[11px]" style={{ color: '#685B72' }}>appointments imported</div>
        </div>
        <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: '#E8E5EC' }} data-testid="status-perf">
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#685B72' }}>Performance</div>
          <div className="font-serif text-2xl mt-1" style={{ color: '#2D2833' }}>{perfMonths.length || 0}</div>
          <div className="text-[11px]" style={{ color: '#685B72' }}>months of data</div>
        </div>
        {user?.role === 'super_user' && (
          <div className="rounded-2xl border p-4 bg-white" style={{ borderColor: '#E8E5EC' }} data-testid="status-bi">
            <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#685B72' }}>Executive BI</div>
            <div className="font-serif text-2xl mt-1" style={{ color: '#2D2833' }}>{biStatus?.months?.length || 0}</div>
            <div className="text-[11px]" style={{ color: '#685B72' }}>months in cube</div>
          </div>
        )}
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {SECTIONS.map(section => (
          <ImportSection
            key={section.id}
            section={section}
            canAccess={canAccess(section)}
            onDone={onDone}
            statsFetch={statsFetchFor(section.id)}
            lastRefreshed={refreshKey}
          />
        ))}
      </div>

      {/* Footer help */}
      <Card className="rounded-2xl border p-5 mt-6 bg-white" style={{ borderColor: '#E8E5EC' }} data-testid="import-help">
        <div className="flex items-start gap-3">
          <FileSpreadsheet size={20} className="shrink-0 mt-0.5" style={{ color: '#685B72' }} />
          <div>
            <h4 className="font-serif text-base" style={{ color: '#2D2833' }}>Quick tips</h4>
            <ol className="mt-2 space-y-1 text-[12px] list-decimal ml-5" style={{ color: '#685B72' }}>
              <li>Drop the wrong file into a zone? You'll get a clear error — nothing is corrupted.</li>
              <li>Re-uploading the same period is safe; the system replaces prior data instead of duplicating.</li>
              <li>For weekly targets, edit inline in <a href="/clinicians" className="underline">Clinician Roster</a> <ArrowRight size={11} className="inline" />.</li>
            </ol>
          </div>
        </div>
      </Card>
    </div>
  );
}
